"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Quote,
  Star,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  Sparkles
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchTestimonials } from "@/store/slices/landingSlice";
import { imageUrl } from "@/services/BaseUrl";

const TestimonialsSection = () => {
  const dispatch = useAppDispatch();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const { data: testimonials, loading: isLoading } = useAppSelector(
    (state) => state.landing.testimonials
  );
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";

  useEffect(() => {
    dispatch(fetchTestimonials());
  }, [dispatch]);

  // Auto-play functionality for Main Testimonial
  useEffect(() => {
    if (!isAutoPlaying || !testimonials || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials]);

  const nextTestimonial = () => {
    if (!testimonials || testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    if (!testimonials || testimonials.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const getFullImageUrl = (image: string): string | null => {
    if (!image) return null;
    if (image.startsWith("http")) return image;
    const path = image.replace(/\\/g, "/");
    return `${imageUrl}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const getInitials = (name: string) => {
    return name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "?";
  };

  if (isLoading && (!testimonials || testimonials.length === 0)) {
    return (
      <div
        className={`py-24 flex items-center justify-center min-h-[600px] transition-colors duration-700 ${
          isDark ? "bg-[#030712]" : "bg-gradient-to-b from-white via-slate-50 to-white"
        }`}
      >
        <motion.div
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-12 h-12 border-4 border-t-transparent rounded-full border-[#8B31FF]"
        />
      </div>
    );
  }

  if (!testimonials || testimonials.length === 0) return null;

  const current = testimonials[currentIndex];

  return (
    <section
      className={`py-24 relative overflow-hidden transition-colors duration-700 ${
        isDark ? "bg-[#030712]" : "bg-gradient-to-b from-white via-slate-50 to-white"
      }`}
    >
      {/* ═══ Border glows ═══ */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent opacity-70" />
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#8B31FF]/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF]/50 to-transparent" />

      {/* ═══ Background ═══ */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {isDark && (
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_55%_at_50%_0%,rgba(79,17,194,0.22),transparent)]" />
        )}

        {/* Grid */}
        <div
          className={`absolute inset-0 ${isDark ? "opacity-[0.055]" : "opacity-[0.035]"}`}
          style={{
            backgroundImage: isDark
              ? `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`
              : `linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        {/* Orb 1 — top-right purple */}
        <motion.div
          className={`absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full blur-[120px] ${
            isDark ? "bg-[#8B31FF]/40" : "bg-[#8B31FF]/10"
          }`}
          animate={{ x: [0, -45, 0], y: [0, 35, 0], scale: [1, 1.12, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 2 — bottom-left cyan */}
        <motion.div
          className={`absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full blur-[120px] ${
            isDark ? "bg-[#8B31FF]/22" : "bg-[#8B31FF]/8"
          }`}
          animate={{ x: [0, 55, 0], y: [0, -35, 0], scale: [1, 1.18, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Orb 3 — centre magenta pulse */}
        <motion.div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full blur-[150px] ${
            isDark ? "bg-[#D946EF]/12" : "bg-[#8B31FF]/5"
          }`}
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`inline-flex items-center gap-1.5 font-bold text-xs uppercase tracking-wider mb-3 px-4 py-1.5 rounded-full transition-all duration-300 ${
              isDark
                ? "text-[#8B31FF] bg-[#8B31FF]/20 border border-[#8B31FF]/30"
                : "text-[#8B31FF] bg-[#8B31FF]/10 border border-purple-200"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Client Testimonials & Feedback
          </motion.span>
          <h2
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.15] ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            What Our{" "}
            <span className="bg-gradient-to-r from-[#8B31FF] via-purple-500 to-[#6d1ec9] bg-clip-text text-transparent">
              Clients Say
            </span>
          </h2>
          <p
            className={`text-base sm:text-lg max-w-2xl mx-auto transition-colors duration-300 ${
              isDark ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Don&apos;t just take our word for it — explore how our digital solutions empower ambitious businesses.
          </p>
        </motion.div>

        {/* Main Testimonial Carousel */}
        {current && (
          <div className="relative max-w-5xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -100, scale: 0.95 }}
                transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="relative"
              >
                <div
                  className={`rounded-lg md:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-12 border relative overflow-hidden transition-all duration-500 ${
                    isDark ? "bg-black/50 border-white/10 backdrop-blur-xl" : "bg-white border-gray-200/80"
                  }`}
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, duration: 0.6, type: "spring" }}
                    className={`absolute top-6 right-6 md:top-8 md:right-8 transition-colors duration-300 ${
                      isDark ? "text-white/5" : "text-[#8B31FF]/10"
                    }`}
                  >
                    <Quote className="w-14 h-14 md:w-24 md:h-24" />
                  </motion.div>

                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                    {/* Avatar */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                      className="flex-shrink-0 relative"
                    >
                      <div
                        className={`w-20 h-20 md:w-32 md:h-32 rounded-full shadow-xl overflow-hidden border-4 flex items-center justify-center relative transition-colors duration-300 ${
                          isDark ? "border-white/10 bg-white/5" : "border-white bg-[#8B31FF]/10"
                        }`}
                      >
                        {current.image ? (
                          <img
                            src={getFullImageUrl(current.image) || ""}
                            alt={current.name}
                            className="w-full h-full object-cover relative z-10"
                            onError={(e) => {
                              const el = e.target as HTMLImageElement;
                              el.onerror = null;
                              el.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                current.name
                              )}&background=4F11C2&color=fff&size=128`;
                            }}
                          />
                        ) : (
                          <span
                            className={`text-xl md:text-3xl font-bold relative z-10 ${
                              isDark ? "text-white" : "text-[#8B31FF]"
                            }`}
                          >
                            {getInitials(current.name)}
                          </span>
                        )}
                      </div>
                    </motion.div>

                    {/* Content */}
                    <div className="flex-1 text-center md:text-left">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="flex justify-center md:justify-start gap-1 mb-3 md:mb-4"
                      >
                        {[...Array(current.rating || 5)].map((_, i) => (
                          <Star
                            key={i}
                            className="w-5 h-5 md:w-6 md:h-6 fill-amber-400 text-amber-400 drop-shadow-md"
                          />
                        ))}
                      </motion.div>

                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className={`text-base md:text-xl leading-relaxed mb-4 md:mb-6 italic ${
                          isDark ? "text-gray-200" : "text-gray-800 font-medium"
                        }`}
                      >
                        &quot;{current.text}&quot;
                      </motion.p>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <h3
                          className={`text-xl md:text-2xl font-bold mb-1 ${
                            isDark ? "text-white" : "text-gray-900"
                          }`}
                        >
                          {current.name}
                        </h3>
                        <p className="bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] bg-clip-text text-transparent font-bold text-sm">
                          {current.role} {current.company ? `· ${current.company}` : ""}
                        </p>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Controls */}
            {testimonials.length > 1 && (
              <div className="flex flex-col items-center gap-3 mt-8">
                <div className="flex items-center justify-center gap-3 w-full max-w-xs sm:max-w-none">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={prevTestimonial}
                    aria-label="Previous testimonial"
                    className="flex-shrink-0 p-3 cursor-pointer md:p-3.5 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white rounded-full shadow-lg hover:shadow-xl transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </motion.button>

                  <div className="flex-1 min-w-0 overflow-hidden">
                    <div
                      className="flex items-center justify-center gap-1.5 overflow-x-auto"
                      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          aria-label={`Go to testimonial ${index + 1}`}
                          className={`flex-shrink-0 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                            index === currentIndex
                              ? "w-7 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9]"
                              : isDark
                              ? "w-2.5 bg-white/20 hover:bg-[#8B31FF]"
                              : "w-2.5 bg-gray-300 hover:bg-[#8B31FF]"
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className="flex-shrink-0 p-3 cursor-pointer md:p-3.5 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white rounded-full shadow-lg hover:shadow-xl transition-all"
                    title={isAutoPlaying ? "Pause" : "Play"}
                  >
                    {isAutoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </motion.button>

                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={nextTestimonial}
                    aria-label="Next testimonial"
                    className="flex-shrink-0 p-3 cursor-pointer md:p-3.5 bg-gradient-to-r from-[#8B31FF] to-[#6d1ec9] text-white rounded-full shadow-lg hover:shadow-xl transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
